<template>
    <v-tooltip
        v-if="!isMobile"
        location="bottom"
        open-delay="200"
    >
        <template #activator="{ props: tooltipProps }">
            <span
                v-bind="tooltipProps"
                class="badge-chip"
                :class="{ 'badge-chip--clickable': isSpecial }"
                :data-testid="`badge-${badge.key}`"
                :role="isSpecial ? 'button' : undefined"
                :tabindex="isSpecial ? 0 : undefined"
                :aria-label="isSpecial ? `Detalji značke ${resolvedTitle}` : undefined"
                @click="isSpecial ? openInfoModal() : undefined"
                @keydown.enter.prevent="isSpecial ? openInfoModal() : undefined"
                @keydown.space.prevent="isSpecial ? openInfoModal() : undefined"
            >
                <AchievementBadge
                    v-if="!progressionDefinition"
                    :tier="BadgeTier.Gold"
                    :type="(badge.definition as SpecialBadgeDefinition).visual"
                    :size="size"
                    :label="resolvedTitle"
                />
                <AchievementBadge
                    v-else
                    :type="progressionBadgeType"
                    :tier="progressionDefinition.tier"
                    :size="size"
                    :label="resolvedTitle"
                    :value="badge.count"
                />
            </span>
        </template>

        <div class="badge-chip__tooltip">
            <div class="badge-chip__title">{{ resolvedTitle }}</div>
            <div class="badge-chip__description">{{ resolvedDescription }}</div>
            <div v-if="currentLine" class="badge-chip__meta">{{ currentLine }}</div>
            <div v-if="awardedLineText" class="badge-chip__meta">{{ awardedLineText }}</div>
            <div v-if="nextLine" class="badge-chip__meta badge-chip__meta--next">{{ nextLine }}</div>
            <div v-if="badge.notes" class="badge-chip__notes">"{{ badge.notes }}"</div>
        </div>
    </v-tooltip>

    <span
        v-else
        role="button"
        tabindex="0"
        class="badge-chip badge-chip--clickable"
        :data-testid="`badge-${badge.key}`"
        :aria-label="`Detalji značke ${resolvedTitle}`"
        @click="openInfoModal"
        @keydown.enter.prevent="openInfoModal"
        @keydown.space.prevent="openInfoModal"
    >
        <AchievementBadge
            v-if="!progressionDefinition"
            :tier="BadgeTier.Gold"
            :type="(badge.definition as SpecialBadgeDefinition).visual"
            :size="size"
            :label="resolvedTitle"
        />
        <AchievementBadge
            v-else
            :type="progressionBadgeType"
            :tier="progressionDefinition.tier"
            :size="size"
            :label="resolvedTitle"
            :value="badge.count"
        />
    </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
    BadgeTier,
    type ProgressionBadgeDefinition,
    type ResolvedUserBadge,
    type SpecialBadgeDefinition,
} from '../../models/Badge';
import { type Gender } from '../../models/User';
import {
    isProgressionBadge,
    resolveBadgeDescription,
    resolveBadgeTitle,
} from '../../utils/badgeCatalog';
import { awardedLine, currentCountLine, nextTierLine } from './badgeDisplayLines';
import AchievementBadge, { type AchievementBadgeType } from './AchievementBadge.vue';
import { isMobileLayout, screenInfo } from '../../utils/isMobileLayout';
import { useModal } from '../../modals/useModal';
import { ModalCode } from '../../modals/ModalCode';
import { storeToRefs } from 'pinia';
import { useGenderStore } from '../../stores/genderStore';

type Props = {
    badge: ResolvedUserBadge;
    size?: number;
    /**
     * Optional gender override for the badge owner — used when the viewer is
     * looking at someone else's badge and we want it shown in that user's
     * preferred form. Defaults to the viewer's own gender preference.
     */
    gender?: Gender;
    /**
     * Live streak so the next-tier hint on streak chips reflects what the
     * user actually needs to do (a streak is only extended by the live run,
     * not the historical best baked into `badge.count`). Ignored for
     * non-streak categories.
     */
    currentStreak?: number;
};

const props = withDefaults(defineProps<Props>(), {
    size: 36,
    gender: undefined,
    currentStreak: undefined,
});

const { showModal } = useModal();
const { gender: viewerGender } = storeToRefs(useGenderStore());

const effectiveGender = computed(() => props.gender ?? viewerGender.value);

const isMobile = computed(() => {
    // Touch screenInfo so the computed stays reactive on resize.
    void screenInfo.width;
    return isMobileLayout();
});

const progressionDefinition = computed<ProgressionBadgeDefinition | null>(() => {
    return isProgressionBadge(props.badge.definition) ? props.badge.definition : null;
});

// Special chips also open the modal on desktop — the tooltip alone has no
// path to the holders page, which is the discovery surface for special badges.
const isSpecial = computed(() => progressionDefinition.value == null);

const progressionBadgeType = computed<AchievementBadgeType>(() => {
    const definition = progressionDefinition.value;
    if (definition?.variant === 'flame') {
        return 'flame';
    }
    if (definition?.variant === '2d6') {
        return '2d6';
    }
    return `d${definition?.dieSides ?? 6}` as AchievementBadgeType;
});

const resolvedTitle = computed(() => resolveBadgeTitle(props.badge.definition, effectiveGender.value));
const resolvedDescription = computed(() =>
    resolveBadgeDescription(props.badge.definition, effectiveGender.value),
);

const currentLine = computed(() => currentCountLine(props.badge));
const awardedLineText = computed(() => awardedLine(props.badge));
const nextLine = computed(() =>
    nextTierLine(props.badge, effectiveGender.value, props.currentStreak),
);

function openInfoModal() {
    // Forward the effective gender so the modal renders in the same form
    // the chip just used — otherwise tapping a non-viewer's badge would
    // reset to the viewer's preference inside the modal. currentStreak
    // travels with it so the modal's next-tier line matches the chip's
    // tooltip for streak badges.
    showModal(ModalCode.BadgeInfo, {
        badge: props.badge,
        gender: props.gender,
        currentStreak: props.currentStreak,
    });
}
</script>

<style lang="scss" scoped>
.badge-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    cursor: help;
}

.badge-chip--clickable {
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid rgb(var(--v-theme-primary));
        outline-offset: 2px;
        border-radius: 4px;
    }
}

.badge-chip__tooltip {
    max-width: 240px;
}

.badge-chip__title {
    font-weight: 700;
    margin-bottom: 2px;
}

.badge-chip__description {
    font-size: 0.85rem;
    opacity: 0.9;
}

.badge-chip__meta {
    margin-top: 4px;
    font-size: 0.75rem;
    opacity: 0.75;
}

.badge-chip__meta--next {
    opacity: 0.95;
    font-weight: 600;
}

.badge-chip__notes {
    margin-top: 4px;
    font-size: 0.75rem;
    font-style: italic;
    opacity: 0.85;
}
</style>
